import React, { useMemo } from "react";
import { imgUrl } from "@/lib/api";

/**
 * MotifCompanions — a flock of photoreal motif cut-outs drifts across the
 * page along 6 organic curved flight paths (see @keyframes motif-path-a..f).
 * Flyers (butterfly/dragonfly/bee) get a wing-flap; the rest gently bob.
 */

const FLYERS = new Set(["butterfly", "dragonfly", "bee"]);

// Variant sets — each has 1..3 PNGs under /uploads/motif-creatures/.
const VARIANTS = {
  butterfly: ["butterfly", "butterfly-2", "butterfly-3"],
  dragonfly: ["dragonfly", "dragonfly-2", "dragonfly-3"],
  bee:       ["bee", "bee-2", "bee-3"],
};

const PATHS = ["a", "b", "c", "d", "e", "f"];

// 14 companions — path, base desktop size, mobile override, variant idx, flip, delay, dur, opacity.
const COMPANIONS = [
  { size: 60, sMob: 34, path: "a", v: 0, flip: false, delay: 0.0,  dur: 22, o: 0.9  },
  { size: 82, sMob: 46, path: "b", v: 1, flip: true,  delay: 2.4,  dur: 26, o: 1.0  },
  { size: 48, sMob: 28, path: "c", v: 2, flip: false, delay: 4.8,  dur: 20, o: 0.85 },
  { size: 96, sMob: 54, path: "d", v: 0, flip: true,  delay: 6.2,  dur: 30, o: 1.0  },
  { size: 68, sMob: 38, path: "e", v: 1, flip: false, delay: 1.6,  dur: 24, o: 0.9  },
  { size: 54, sMob: 30, path: "f", v: 2, flip: true,  delay: 5.2,  dur: 26, o: 0.85 },
  { size: 78, sMob: 44, path: "a", v: 1, flip: false, delay: 9.4,  dur: 28, o: 0.95 },
  { size: 44, sMob: 26, path: "c", v: 0, flip: true,  delay: 3.0,  dur: 18, o: 0.8  },
  { size: 88, sMob: 50, path: "b", v: 2, flip: false, delay: 11.0, dur: 30, o: 1.0  },
  { size: 56, sMob: 32, path: "d", v: 1, flip: true,  delay: 2.8,  dur: 22, o: 0.9  },
  { size: 74, sMob: 40, path: "e", v: 2, flip: false, delay: 6.8,  dur: 26, o: 0.95 },
  { size: 46, sMob: 28, path: "a", v: 0, flip: true,  delay: 8.4,  dur: 20, o: 0.8  },
  { size: 62, sMob: 36, path: "f", v: 1, flip: false, delay: 0.8,  dur: 24, o: 0.85 },
  { size: 52, sMob: 30, path: "c", v: 2, flip: true,  delay: 7.2,  dur: 22, o: 0.85 },
];

const MotifCompanions = ({ motif }) => {
  const canFlap = FLYERS.has(motif);
  const variantNames = VARIANTS[motif] || [motif];
  const srcFor = (idx) => imgUrl(`/api/uploads/motif-creatures/${variantNames[idx % variantNames.length]}.png`);
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  React.useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const nodes = useMemo(() =>
    COMPANIONS.map((c, i) => {
      const outerStyle = {
        opacity: c.o,
        animation: `motif-path-${c.path} ${c.dur}s cubic-bezier(0.42, 0, 0.58, 1) ${c.delay}s infinite`,
      };
      const flipX = c.flip ? -1 : 1;
      const size = isMobile ? c.sMob : c.size;
      const innerStyle = canFlap
        ? {
            animation: `motif-flap ${1.05 + (i % 4) * 0.18}s ease-in-out infinite`,
            transformOrigin: "50% 50%",
            transform: `scaleX(${flipX})`,
          }
        : {
            animation: `motif-bob ${2.4 + (i % 3) * 0.4}s ease-in-out infinite`,
            transform: `scaleX(${flipX})`,
          };

      return (
        <div
          key={`${motif}-${i}`}
          className="absolute pointer-events-none will-change-transform"
          style={outerStyle}
          data-testid={`motif-companion-${i}`}
          data-path={c.path}
        >
          <div style={innerStyle}>
            <img
              src={srcFor(c.v)}
              alt=""
              width={size}
              height={size}
              className="block select-none"
              draggable="false"
              style={{
                width: size,
                height: "auto",
                filter: "drop-shadow(0 4px 8px rgba(28,26,25,0.14))",
              }}
              loading="lazy"
            />
          </div>
        </div>
      );
    }),
  [motif, canFlap, isMobile]);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid="motif-companions"
    >
      {nodes}
    </div>
  );
};

export default MotifCompanions;
